import React, { memo, useEffect } from "react";
import { Controller, useFieldArray } from "react-hook-form";
import RequiredTitle from "./RequiredTitle";

const YouTubeUrlsField = ({ input, control, error, defaultValue }) => {
  const {
    name,
    label,
    validation,
    defaultValues = defaultValue,
    alignment = {},
    placeholder,
  } = input;
  const { sm = 12, md = 12, lg = 12 } = alignment;

  const { fields, append, remove, replace } = useFieldArray({
    control,
    name,
  });

  useEffect(() => {
    if (defaultValues?.length > 0) {
      replace(defaultValues);
    } else {
      replace([""]);
    }
  }, [defaultValues, replace]);

  // Function to extract YouTube video ID from URL
  const extractYouTubeId = (url) => {
    if (!url) return null;
    
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
      /youtube\.com\/watch\?.*v=([^&\n?#]+)/,
    ];
    
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) return match[1];
    }
    return null;
  };

  const validateYouTubeUrl = (url) => {
    const videoId = extractYouTubeId(url);
    return videoId && videoId.length === 11;
  };

  const addRow = () => {
    append("");
  };

  const removeRow = (index) => {
    if (fields.length > 1) {
      remove(index);
    }
  };

  return (
    <div className={`form-group mb-3 col-sm-${sm} col-md-${md} col-lg-${lg}`}>
      <RequiredTitle
        label={label}
        validation={validation}
      />
      <div>
        {fields.map((field, index) => (
          <Controller
            key={field.id}
            name={`${name}[${index}]`}
            control={control}
            rules={{
              required: validation?.required ? "YouTube URL is required" : false,
              validate: (value) => {
                if (validation?.required && !value) return "YouTube URL is required";
                if (validation?.required && !validateYouTubeUrl(value)) {
                  return "Please enter a valid YouTube URL";
                }
                return true;
              },
              ...validation,
            }}
            render={({ field: controllerField, fieldState }) => (
              <div className="d-flex align-items-center mb-2">
                <div className="flex-grow-1 position-relative">
                  <input
                    type="text"
                    className={`form-control ${fieldState.error ? "is-invalid" : ""}`}
                    placeholder={placeholder || "https://www.youtube.com/watch?v=VIDEO_ID"}
                    {...controllerField}
                  />
                  {fieldState.error && (
                    <div className="invalid-feedback d-block">
                      {fieldState.error.message}
                    </div>
                  )}
                </div>
                <span
                  className="btn-icon-start-danger ms-2"
                  onClick={() => removeRow(index)}
                  style={{ cursor: "pointer" }}
                >
                  <i className="fa fa-trash color-info" />
                </span>
              </div>
            )}
          />
        ))}
        <span
          className="btn-icon-start-danger ms-1 mt-1"
          onClick={addRow}
          style={{ cursor: "pointer" }}
        >
          <i className="fa fa-plus color-info" />
        </span>
      </div>
      {error && <div className="invalid-feedback">{error}</div>}
    </div>
  );
};

export default memo(YouTubeUrlsField); 